import { useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";

import { useGetRallies } from "../../../../../api/jeep-rally/rally/rally.hooks";
import {
  useGetRouteById,
  useUpdateRouteMutation,
} from "../../../../../api/jeep-rally/routes/routes.hooks";
import { isJeepRallyAdmin } from "../../Categories/utils/category.utils";
import { normalizeRallies } from "../../Rallies/utils/rally.utils";
import RouteForm from "../_components/RouteForm";
import {
  buildRoutePayload,
  getRouteApiErrorMessage,
  initialRouteForm,
  mapRouteToForm,
  normalizeRoute,
  validateRouteForm,
} from "../utils/route.utils";

const RouteUpdatePage = () => {
  const history = useHistory();
  const { id } = useParams();
  const canManage = isJeepRallyAdmin();

  const locationRoute = history.location.state?.route || null;

  const {
    data: routeData,
    isLoading: isRouteLoading,
    isError: isRouteError,
    error: routeError,
  } = useGetRouteById(id);

  const route = normalizeRoute(routeData) || locationRoute;

  const [form, setForm] = useState(initialRouteForm);
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const {
    mutateAsync: updateRoute,
    isLoading,
    isPending,
    reset,
  } = useUpdateRouteMutation();

  const { data: ralliesData, isLoading: isRalliesLoading } = useGetRallies();
  const rallies = useMemo(
    () => normalizeRallies(ralliesData),
    [ralliesData]
  );

  const submitting = isLoading || isPending;

  useEffect(() => {
    const next = normalizeRoute(routeData) || locationRoute;
    if (!next) return;
    setForm(mapRouteToForm(next));
    setValidationErrors({});
  }, [routeData, locationRoute]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManage || !id) return;

    reset();
    setSubmitError("");

    const nextErrors = validateRouteForm(form);
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      await updateRoute({
        routeId: id,
        payload: buildRoutePayload(form, { isEdit: true }),
      });
      history.push("/jeep-rally/routes");
    } catch (error) {
      setSubmitError(
        getRouteApiErrorMessage(error, "Failed to update route.")
      );
    }
  };

  const goBack = () => history.push("/jeep-rally/routes");

  if (isRouteLoading && !locationRoute) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if ((isRouteError && !locationRoute) || !route) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {getRouteApiErrorMessage(
                routeError,
                "Route not found. Please open it from the Routes list."
              )}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to routes list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">Update Route</h4>
          </div>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            Update route metadata and upsert stages. Stages with an existing{" "}
            <code>_id</code> are updated; new stages are created. Omitted stages
            are not deleted. Checkpoints are managed from the Rallies list.
          </p>

          {!canManage && (
            <Alert variant="warning" className="mb-4">
              Admin access is required to update routes.
            </Alert>
          )}

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError}
            </Alert>
          )}

          <RouteForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            submitLabel="Update Route"
            submittingLabel="Updating..."
            readOnly={!canManage}
            rallies={rallies}
            isRalliesLoading={isRalliesLoading}
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onBack={goBack}
          />
        </div>
      </div>
    </div>
  );
};

export default RouteUpdatePage;
